/**
 * Одноразовый репэйр категорий бюджета: чинит НЕвалидные (не-GUID) id категорий, схлопывает
 * дубликаты по контенту (type+label+icon), перепривязывает транзакции.
 *
 * Причина: старые бюджеты содержат легаси-строковые id (DEFAULT_CATS: 'food'/'health'/…) и GUID'ы
 * с префиксом 'c' (баг edit-form: 'c' + uuid). Сервер синка ждёт Guid → операции по таким категориям
 * отклоняются (MalformedOperation), а сами категории дублируются с серверной версией.
 *
 * Стратегия: для группы категорий с одинаковым контентом выбираем каноническую с ВАЛИДНЫМ GUID
 * (если серверный близнец уже подтянут в IDB — это его id, дубль на сервере не создаётся). Если в
 * группе нет ни одного валидного GUID — минтим свежий. Все прочие id группы → канонический.
 *
 * Чистая функция (мутирует переданный объект и возвращает его) — тестируется изолированно.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (v) => !!v && UUID_RE.test(v);
const contentKey = (c) => `${c.type}|${c.label}|${c.icon}`;
export function repairBudgetCategories(budget) {
    const idMap = new Map();
    // ВАЖНО: трогаем ТОЛЬКО категории с невалидным (не-GUID) id. Категории с валидным GUID
    // не сливаем и не удаляем — иначе на НОРМАЛЬНОМ бюджете две легитимные категории с одинаковым
    // контентом (type+label+icon) схлопнулись бы в одну (потеря данных).
    // Первый валидный GUID для каждого контента — цель, куда «перенимаем» битые близнецы (обычно
    // это серверная категория, уже подтянутая bootstrap-гидрацией).
    const validIdByContent = new Map();
    for (const c of budget.categories) {
        if (isUuid(c.id)) {
            const key = contentKey(c);
            if (!validIdByContent.has(key))
                validIdByContent.set(key, c.id);
        }
    }
    const result = [];
    const mintedByContent = new Map(); // контент → свежий GUID (битые без валидного близнеца)
    for (const c of budget.categories) {
        if (isUuid(c.id)) {
            result.push(c); // валидные оставляем как есть (в т.ч. легитимные контент-дубли)
            continue;
        }
        const key = contentKey(c);
        const validTwin = validIdByContent.get(key);
        if (validTwin) {
            // Битый близнец валидной категории → перенимаем её id, саму битую выкидываем.
            idMap.set(c.id, validTwin);
            continue;
        }
        // Битая категория без валидного близнеца: первую оставляем со свежим GUID, дубли схлопываем.
        let cid = mintedByContent.get(key);
        if (!cid) {
            cid = crypto.randomUUID();
            mintedByContent.set(key, cid);
            result.push({ ...c, id: cid, version: c.version ?? 1 });
        }
        idMap.set(c.id, cid);
    }
    // Перепривязка транзакций ТОЛЬКО по idMap (битый id → канонический). Прочие ссылки не трогаем —
    // миграция не должна менять валидные/висячие ссылки нормального бюджета.
    for (const t of budget.transactions) {
        if (t.category != null && idMap.has(t.category)) {
            t.category = idMap.get(t.category);
        }
    }
    const changed = idMap.size > 0 || result.length !== budget.categories.length;
    budget.categories = result;
    return { budget, idMap, changed };
}
